// @ts-nocheck
import { useChat } from '@ai-sdk/react';
import { useEffect, useRef, useState } from 'react';
import { useParams } from '@tanstack/react-router';
import { X, Send, Sparkles, Loader2, Mic, Square } from 'lucide-react';
import { useStore } from '../store/useStore.js';
import { AIConsentModal } from './AIConsentModal.js';
import { runAgent } from '../ai/agentClient.js';
import { applyAgentActions } from '../ai/agentActions.js';
import { useRealtimeVoice } from '../ai/useRealtimeVoice.js';

function MIcon({ name, size = 18 }: { name: string; size?: number }) {
  return <span className="material-symbols-outlined" style={{ fontSize: size, lineHeight: 1 }}>{name}</span>;
}

export function AIChatDrawer() {
  const open = useStore(s => s.chatOpen);
  const setChatOpen = useStore(s => s.setChatOpen);
  const onClose = () => setChatOpen(false);

  const { documentId: urlDocumentId } = useParams({ strict: false }) as { documentId?: string };
  const workspaceId = useStore(s => s.workspaceId);
  const selectedId = useStore(s => s.selectedId);
  const nodes = useStore(s => s.nodes);
  const [showConsent, setShowConsent] = useState(false);
  const [agentMode, setAgentMode] = useState(true);
  const [agentBusy, setAgentBusy] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const activeId = selectedId || urlDocumentId;
  const activeNode = nodes.find(n => n.id === activeId);
  const selectedContent = activeNode
    ? `[Contexto da Nota: ${activeNode.label}]\n${activeNode.content || ''}`
    : '';

  const { messages, input, handleInputChange, handleSubmit, isLoading, error, setMessages } = useChat({
    api: '/api/ai/rag/query',
    body: { workspaceId },
    fetch: async (url, options) => {
      let body;
      try {
        body = JSON.parse(options?.body as string);
      } catch (e) {
        console.error('Falha ao parsear corpo da requisição do chat:', e);
        throw e;
      }
      const lastMessage = body.messages && body.messages.length > 0
        ? body.messages[body.messages.length - 1]
        : { content: '' };
      const queryWithContext = selectedContent
        ? `${selectedContent}\n\nPergunta do usuário: ${lastMessage?.content ?? ''}`
        : (lastMessage?.content ?? '');
      return fetch(url, {
        ...options,
        body: JSON.stringify({ workspaceId, query: queryWithContext }),
        credentials: 'include',
      });
    },
    onError: (err) => {
      if (err.message.includes('ai_consent_required') || err.message.includes('403')) {
        setShowConsent(true);
      }
    },
  });

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, isLoading]);

  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  const voice = useRealtimeVoice({ workspaceId, onUtterance: handleVoiceUtterance });

  // Roda um turno do agente: empurra a fala/texto do usuário, chama /ai/agent,
  // aplica as ações no grafo e mostra a resposta. Retorna o texto da resposta (p/ TTS).
  async function runAgentTurn(q: string): Promise<string> {
    const history = (messages || [])
      .filter((m) => m.role === 'user' || m.role === 'assistant')
      .slice(-6)
      .map((m) => ({ role: m.role, content: m.content }));
    const st = useStore.getState();
    const recent = st.nodes.slice(-12).map((n) => n.label).filter(Boolean);
    const ctxParts = [];
    if (selectedContent) ctxParts.push(selectedContent);
    if (recent.length) ctxParts.push(`Nós existentes (mais recentes por último): ${recent.join(', ')}.`);
    const ctx = ctxParts.join('\n') || undefined;

    setMessages((prev) => [...prev, { id: crypto.randomUUID(), role: 'user', content: q }]);
    const { reply, actions } = await runAgent(workspaceId, q, ctx, history);
    const summary = applyAgentActions(actions);
    const content = [reply, summary].filter(Boolean).join('\n\n') || '(sem resposta)';
    setMessages((prev) => [...prev, { id: crypto.randomUUID(), role: 'assistant', content }]);
    return reply || '';
  }

  async function handleVoiceUtterance(text: string): Promise<string> {
    try {
      await runAgentTurn(text);
    } catch (err) {
      const msg = (err as Error)?.message ?? 'falha';
      if (msg.includes('ai_consent_required') || msg.includes('403')) setShowConsent(true);
      else setMessages((prev) => [...prev, { id: crypto.randomUUID(), role: 'assistant', content: 'Erro: ' + msg }]);
    }
    return ''; // execute-only: sem TTS
  }

  async function handleAgentSubmit(e: React.FormEvent) {
    e.preventDefault();
    const q = input?.trim();
    if (!q || agentBusy) return;
    handleInputChange({ target: { value: '' } } as React.ChangeEvent<HTMLInputElement>);
    setAgentBusy(true);
    try {
      await runAgentTurn(q);
    } catch (err) {
      const msg = (err as Error)?.message ?? 'falha';
      if (msg.includes('ai_consent_required') || msg.includes('403')) setShowConsent(true);
      else setMessages((prev) => [...prev, { id: crypto.randomUUID(), role: 'assistant', content: 'Erro: ' + msg }]);
    } finally {
      setAgentBusy(false);
    }
  }

  const voiceActive = voice.status !== 'idle' && voice.status !== 'error';
  const busy = isLoading || agentBusy;

  if (!open) return null;

  return (
    <>
      <aside style={shellStyle}>
        {/* Header */}
        <header style={headerStyle}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
            <div style={iconBadgeStyle}>
              <Sparkles size={14} />
            </div>
            <div style={{ minWidth: 0, display: 'flex', flexDirection: 'column' }}>
              <span style={titleStyle}>Neurology IA</span>
              {activeNode && (
                <span style={subtitleStyle} title={activeNode.label}>
                  Contexto · {activeNode.label}
                </span>
              )}
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <button
              onClick={() => setAgentMode((v) => !v)}
              title={agentMode ? 'Modo Agente: cria nós, tarefas e ligações' : 'Modo Busca: pergunta ao grafo'}
              style={{
                ...closeBtnStyle,
                width: 'auto',
                padding: '0 10px',
                fontSize: 11,
                fontWeight: 700,
                fontFamily: 'inherit',
                color: agentMode ? '#02131a' : 'var(--muted)',
                background: agentMode ? 'var(--neon)' : 'transparent',
                borderColor: agentMode ? 'var(--neon)' : 'var(--panel-border)',
              }}
            >
              {agentMode ? 'Agente' : 'Busca'}
            </button>
            <button onClick={onClose} aria-label="Fechar" style={closeBtnStyle}>
              <X size={16} />
            </button>
          </div>
        </header>

        {/* Messages */}
        <div style={bodyStyle}>
          {messages.length === 0 && (
            <div style={emptyStateStyle}>
              <div style={emptyOrbStyle}>
                <MIcon name="neurology" size={28} />
              </div>
              <h3 style={emptyTitleStyle}>Como posso ajudar?</h3>
              <p style={emptySubStyle}>
                {activeNode
                  ? `Pergunte sobre "${activeNode.label}".`
                  : 'Pesquise no seu grafo de conhecimento ou abra um nó pra contexto.'}
              </p>
            </div>
          )}

          {messages.map((m) => (
            <div
              key={m.id}
              style={{
                display: 'flex',
                justifyContent: m.role === 'user' ? 'flex-end' : 'flex-start',
              }}
            >
              <div
                style={
                  m.role === 'user' ? bubbleUserStyle : bubbleAiStyle
                }
              >
                {m.content}
              </div>
            </div>
          ))}

          {busy && (
            <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
              <div style={bubbleLoadingStyle}>
                <Loader2 size={13} style={{ animation: 'spin 1s linear infinite' }} />
                <span>{agentMode ? 'Agente trabalhando…' : 'Sincronizando neurônios…'}</span>
              </div>
            </div>
          )}

          {error && !error.message.includes('403') && (
            <div style={errorBoxStyle}>
              Erro: {error.message}
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* Voz: status + transcript ao vivo */}
        {(voiceActive || voice.status === 'connecting') && (
          <div style={voiceBarStyle}>
            <span style={voiceDotStyle(voice.status)} />
            <span style={{ fontWeight: 700, fontSize: 11 }}>{voiceLabel(voice.status)}</span>
            {voice.transcript && (
              <span
                style={{
                  opacity: 0.85,
                  fontSize: 11.5,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                “{voice.transcript}”
              </span>
            )}
          </div>
        )}
        {voice.error && <div style={{ ...errorBoxStyle, margin: '0 12px 8px' }}>{voice.error}</div>}

        {/* Composer */}
        <form onSubmit={agentMode ? handleAgentSubmit : handleSubmit} style={composerStyle}>
          <button
            type="button"
            onClick={() => (voiceActive || voice.status === 'connecting' ? voice.stop() : voice.start())}
            title={voiceActive ? 'Encerrar voz' : 'Conversar por voz'}
            aria-label={voiceActive ? 'Encerrar voz' : 'Conversar por voz'}
            style={micBtnStyle(voice.status)}
          >
            {voiceActive || voice.status === 'connecting' ? <Square size={14} /> : <Mic size={16} />}
          </button>
          <input
            value={input}
            onChange={handleInputChange}
            placeholder={
              agentMode
                ? 'Fale o que criar… ex: "crie 3 tarefas pro lançamento"'
                : activeNode
                  ? 'Perguntar sobre esta nota…'
                  : 'Pesquise no seu grafo…'
            }
            disabled={busy}
            style={inputStyle}
          />
          <button
            type="submit"
            disabled={busy || !input?.trim()}
            aria-label="Enviar"
            style={sendBtnStyle(busy || !input?.trim())}
          >
            <Send size={15} />
          </button>
        </form>
      </aside>

      <AIConsentModal open={showConsent} onClose={() => setShowConsent(false)} />

      <style>{`
        @keyframes spin { from { transform: rotate(0); } to { transform: rotate(360deg); } }
        @keyframes voice-pulse { 0%,100% { opacity: 1; } 50% { opacity: 0.4; } }
        @keyframes drawer-in {
          from { transform: translateX(-12px); opacity: 0; }
          to { transform: none; opacity: 1; }
        }
        .syn-chat-msg-list::-webkit-scrollbar { width: 6px; }
        .syn-chat-msg-list::-webkit-scrollbar-thumb { background: rgba(var(--neon-rgb), 0.18); border-radius: 999px; }
        .syn-chat-msg-list::-webkit-scrollbar-thumb:hover { background: rgba(var(--neon-rgb), 0.32); }
      `}</style>
    </>
  );
}

const shellStyle: React.CSSProperties = {
  position: 'fixed',
  top: 'calc(var(--topbar-h) + 8px)',
  bottom: 12,
  left: 'calc(var(--sidebar-w) + 8px)',
  width: 'var(--panel-w)',
  background: 'var(--panel)',
  border: '1px solid var(--panel-border)',
  borderLeft: '1px solid rgba(var(--neon-rgb), 0.22)',
  borderRadius: 'var(--r-lg)',
  boxShadow: '0 16px 48px rgba(0,0,0,0.55), 0 0 24px rgba(var(--neon-rgb), 0.08)',
  backdropFilter: 'blur(28px) saturate(160%)',
  WebkitBackdropFilter: 'blur(28px) saturate(160%)',
  display: 'flex',
  flexDirection: 'column',
  zIndex: 120,
  overflow: 'hidden',
  fontFamily: 'var(--font-display)',
  animation: 'drawer-in .22s var(--ease-out)',
};

const headerStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 8,
  padding: '12px 14px',
  borderBottom: '1px solid var(--panel-border)',
  background: 'linear-gradient(180deg, rgba(var(--neon-rgb), 0.06), transparent)',
};

const iconBadgeStyle: React.CSSProperties = {
  width: 28, height: 28,
  borderRadius: 8,
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  background: 'rgba(var(--neon-rgb), 0.12)',
  border: '1px solid rgba(var(--neon-rgb), 0.28)',
  color: 'var(--neon)',
  boxShadow: '0 0 12px rgba(var(--neon-rgb), 0.22)',
  flexShrink: 0,
};

const titleStyle: React.CSSProperties = {
  fontSize: 13,
  fontWeight: 700,
  letterSpacing: '-0.005em',
  color: 'var(--text)',
};

const subtitleStyle: React.CSSProperties = {
  fontSize: 10.5,
  color: 'var(--muted)',
  textTransform: 'uppercase',
  letterSpacing: '0.06em',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  maxWidth: 230,
};

const closeBtnStyle: React.CSSProperties = {
  width: 26, height: 26,
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  background: 'transparent',
  border: '1px solid var(--panel-border)',
  borderRadius: 8,
  color: 'var(--muted)',
  cursor: 'pointer',
  transition: 'all .15s',
};

const bodyStyle: React.CSSProperties = {
  flex: 1,
  overflowY: 'auto',
  padding: '14px',
  display: 'flex',
  flexDirection: 'column',
  gap: 10,
  scrollbarWidth: 'thin',
};

const emptyStateStyle: React.CSSProperties = {
  textAlign: 'center',
  margin: 'auto 0',
  padding: '0 12px',
  color: 'var(--muted)',
};

const emptyOrbStyle: React.CSSProperties = {
  width: 56, height: 56,
  borderRadius: '50%',
  background: 'radial-gradient(circle at 35% 30%, rgba(var(--neon-rgb), 0.28), rgba(var(--neon-rgb), 0.04) 70%, transparent)',
  border: '1px solid rgba(var(--neon-rgb), 0.22)',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: 'var(--neon)',
  marginBottom: 12,
  boxShadow: '0 0 24px rgba(var(--neon-rgb), 0.22)',
};

const emptyTitleStyle: React.CSSProperties = {
  fontSize: 15,
  fontWeight: 600,
  color: 'var(--text)',
  marginBottom: 4,
};

const emptySubStyle: React.CSSProperties = {
  fontSize: 11.5,
  color: 'var(--muted)',
  lineHeight: 1.55,
  maxWidth: 240,
  margin: '0 auto',
};

const bubbleBase: React.CSSProperties = {
  maxWidth: '88%',
  padding: '8px 12px',
  fontSize: 12.5,
  lineHeight: 1.55,
  borderRadius: 12,
  letterSpacing: '-0.002em',
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-word',
};

const bubbleUserStyle: React.CSSProperties = {
  ...bubbleBase,
  background: 'linear-gradient(135deg, rgba(var(--neon-rgb), 0.22), rgba(var(--neon-rgb), 0.12))',
  color: 'var(--text)',
  border: '1px solid rgba(var(--neon-rgb), 0.32)',
  borderTopRightRadius: 4,
  boxShadow: '0 4px 14px rgba(var(--neon-rgb), 0.10)',
};

const bubbleAiStyle: React.CSSProperties = {
  ...bubbleBase,
  background: 'rgba(255,255,255,0.04)',
  color: 'var(--text)',
  border: '1px solid var(--panel-border)',
  borderTopLeftRadius: 4,
};

const bubbleLoadingStyle: React.CSSProperties = {
  ...bubbleBase,
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  background: 'rgba(255,255,255,0.03)',
  color: 'var(--muted)',
  border: '1px dashed rgba(var(--neon-rgb), 0.22)',
  borderTopLeftRadius: 4,
};

const errorBoxStyle: React.CSSProperties = {
  padding: '8px 10px',
  fontSize: 11,
  color: '#fca5a5',
  background: 'rgba(248, 113, 113, 0.08)',
  border: '1px solid rgba(248, 113, 113, 0.25)',
  borderRadius: 8,
  textAlign: 'center',
};

const composerStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  padding: '10px 12px',
  borderTop: '1px solid var(--panel-border)',
  background: 'rgba(255,255,255,0.02)',
};

const inputStyle: React.CSSProperties = {
  flex: 1,
  height: 36,
  padding: '0 12px',
  background: 'rgba(255,255,255,0.04)',
  border: '1px solid var(--panel-border)',
  borderRadius: 10,
  color: 'var(--text)',
  fontSize: 13,
  fontFamily: 'inherit',
  outline: 'none',
  transition: 'border-color .15s, box-shadow .15s',
};

function sendBtnStyle(disabled: boolean): React.CSSProperties {
  return {
    width: 36, height: 36,
    display: 'inline-flex',
    alignItems: 'center', justifyContent: 'center',
    background: disabled ? 'rgba(var(--neon-rgb), 0.12)' : 'var(--neon)',
    color: disabled ? 'rgba(var(--neon-rgb), 0.5)' : '#02131a',
    border: 'none',
    borderRadius: 10,
    cursor: disabled ? 'not-allowed' : 'pointer',
    boxShadow: disabled ? 'none' : '0 0 16px rgba(var(--neon-rgb), 0.35)',
    transition: 'transform .12s, box-shadow .12s',
    flexShrink: 0,
  };
}

function voiceLabel(status: string): string {
  switch (status) {
    case 'connecting': return 'Conectando…';
    case 'listening': return 'Ouvindo';
    case 'thinking': return 'Pensando…';
    case 'speaking': return 'Falando…';
    default: return '';
  }
}

const VOICE_COLOR: Record<string, string> = {
  connecting: '#fbbf24',
  listening: '#39ff14',
  thinking: '#00f5ff',
  speaking: '#ff00e5',
};

function voiceDotStyle(status: string): React.CSSProperties {
  const c = VOICE_COLOR[status] ?? 'var(--muted)';
  return {
    width: 8, height: 8, borderRadius: '50%',
    background: c,
    boxShadow: `0 0 8px ${c}`,
    flexShrink: 0,
    animation: status === 'listening' || status === 'speaking' ? 'voice-pulse 1.2s ease-in-out infinite' : undefined,
  };
}

const voiceBarStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  padding: '8px 12px',
  margin: '0 12px 8px',
  borderRadius: 10,
  background: 'rgba(var(--neon-rgb), 0.06)',
  border: '1px solid rgba(var(--neon-rgb), 0.18)',
  color: 'var(--text)',
  minWidth: 0,
};

function micBtnStyle(status: string): React.CSSProperties {
  const active = status !== 'idle' && status !== 'error';
  const c = VOICE_COLOR[status] ?? 'var(--neon)';
  return {
    width: 36, height: 36,
    display: 'inline-flex',
    alignItems: 'center', justifyContent: 'center',
    background: active ? `${c}22` : 'transparent',
    color: active ? c : 'var(--muted)',
    border: `1px solid ${active ? c : 'var(--panel-border)'}`,
    borderRadius: 10,
    cursor: 'pointer',
    transition: 'all .15s',
    flexShrink: 0,
  };
}
